"use client";

import { createContext, useContext, useEffect, useRef } from "react";
import { Gift, Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import { SportsbookLogoMark } from "@/components/sportsbook-logos";
import { sportsbookIdToDisplay } from "@/lib/sportsbook-registry";
import type { Flash } from "@/lib/odds-screen/live/derive";
import type { BookId, StakeKind } from "@/lib/odds-screen/live/types";
import type { CellSide } from "./row-model";
import { formatStake, type StakeDisplay } from "./labels";
import { signupOffer, type BetPick } from "./bet";
import type { CellBet, CellTarget } from "./board-rows";
import { MinStakeContext, belowMinText } from "./use-min-stake";

function FlashOverlay({ flash, display }: { flash: Flash; display: string }) {
  const ref = useRef<HTMLSpanElement | null>(null);
  useEffect(() => {
    if (flash === null) return;
    const element = ref.current;
    if (element === null || typeof element.animate !== "function") return;
    if (typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    element.animate([{ opacity: 0.3 }, { opacity: 0.3, offset: 0.17, easing: "ease" }, { opacity: 0 }], { duration: 900 });
  }, [flash, display]);
  return (
    <span
      ref={ref}
      aria-hidden="true"
      data-flash={flash ?? undefined}
      className={cn(
        "pointer-events-none absolute inset-0 rounded-md opacity-0",
        flash === "up" && "bg-accent-value",
        flash === "down" && "bg-destructive",
      )}
    />
  );
}

export function StackedLogos({ books, size, ringColor, max = 3, hideOverflow = false, className }: { books: readonly string[]; size: number; ringColor: string; max?: number; hideOverflow?: boolean; className?: string }) {
  const shown = books.slice(0, max);
  const more = books.length - shown.length;
  return (
    <span className={cn("isolate flex items-center", className)}>
      {shown.map((book, index) => (
        <span
          key={book}
          className="relative inline-flex rounded"
          style={{ marginLeft: index === 0 ? 0 : -6, zIndex: shown.length - index, boxShadow: `0 0 0 1.5px ${ringColor}` }}
        >
          <SportsbookLogoMark sportsbook={book} size={size} decorative />
        </span>
      ))}
      {!hideOverflow && more > 0 && <span className="data-num ml-1 text-[11px] text-zinc-500">+{more}</span>}
    </span>
  );
}

export const BookStakeContext = createContext<ReadonlyMap<BookId, StakeKind | null>>(new Map());

export function stakeLabel(stake: StakeDisplay | null): string {
  return stake === null ? "" : `, ${stake.spoken}`;
}

export function StakeText({ stake, className }: { stake: StakeDisplay | null; className?: string }) {
  if (stake === null) return null;
  return <span className={cn("data-num whitespace-nowrap text-[10px] font-medium leading-3 text-zinc-500", className)}>{stake.text}</span>;
}

export function ExternalArrow({ className }: { className?: string }) {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
      <path d="M7 17 17 7" />
      <path d="M8 7h9v9" />
    </svg>
  );
}

export function betLabel(pick: BetPick, subject: string, what: string, held = false): string {
  const name = sportsbookIdToDisplay(pick.book);
  const bet = `${subject ? `${subject}, ` : ""}${what} at ${name}${held ? ", paused" : ""}`;
  if (!pick.signup) return bet;
  const offer = signupOffer(pick.book);
  return `${bet}, opens the ${name} sign-up offer${offer ? `. ${offer.details}` : ""}`;
}

export function BetChip({
  books, pick, subject, what, logoSize, ringColor, max, stacked = false, held = false, className, onBet, onPrefetch,
}: {
  books: readonly BookId[];
  pick: BetPick;
  subject: string;
  what: string;
  logoSize: number;
  ringColor: string;
  max: number;
  stacked?: boolean;
  held?: boolean;
  className?: string;
  onBet: () => void;
  onPrefetch: () => void;
}) {
  const label = betLabel(pick, subject, what, held);
  return (
    <button
      type="button"
      onClick={onBet}
      onPointerEnter={onPrefetch}
      onPointerDown={onPrefetch}
      onFocus={onPrefetch}
      aria-label={label}
      title={label}
      className={cn("group/bet flex flex-shrink-0 items-center justify-center transition-colors duration-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40", stacked ? "flex-col gap-0.5" : "gap-1", className)}
    >
      <StackedLogos books={[pick.book, ...books.filter((book) => book !== pick.book)]} size={logoSize} ringColor={ringColor} max={max} hideOverflow className={held ? "opacity-40" : undefined} />
      {held
        ? <Lock size={10} strokeWidth={2.5} aria-hidden="true" className="flex-shrink-0 text-zinc-400" />
        : pick.signup
        ? <Gift size={10} strokeWidth={2.5} aria-hidden="true" className="flex-shrink-0 text-accent-value" />
        : <ExternalArrow className="flex-shrink-0 text-zinc-500 transition-colors duration-100 [@media(hover:hover)]:group-hover/bet:text-primary" />}
    </button>
  );
}

export function PriceCell({
  side,
  flash,
  selected,
  onOpen,
  bet,
  target,
  logoSize = 16,
  ringColor = "#16171a",
  compact = false,
}: {
  side: CellSide;
  flash: Flash;
  selected: boolean;
  onOpen: () => void;
  bet: CellBet;
  target: CellTarget;
  logoSize?: number;
  ringColor?: string;
  compact?: boolean;
}) {
  const kinds = useContext(BookStakeContext);
  const minStake = useContext(MinStakeContext);
  if (side.best === null && side.belowMin) {
    return (
      <button
        type="button"
        onClick={onOpen}
        aria-label={`${side.sideLabel}, ${belowMinText(minStake).toLowerCase()}. Compare all books`}
        title={belowMinText(minStake)}
        className={cn("flex w-full items-center justify-center rounded-md border border-transparent text-[13px] text-zinc-600 transition-colors duration-100 hover:bg-white/[0.065] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40", compact ? "h-11" : "h-[30px]", selected && "border-primary/55 bg-primary/10")}
        style={{ gridColumn: "1 / -1" }}
      >
        &mdash;
      </button>
    );
  }
  if (side.best === null) {
    return (
      <div aria-label={`${side.sideLabel} not offered`} className={cn("flex w-full items-center justify-center rounded-md text-[13px] text-zinc-600", compact ? "h-11" : "h-[30px]")} style={{ gridColumn: "1 / -1" }}>
        &mdash;
      </div>
    );
  }
  const held = side.best.held === true;
  const choice = bet.pick(side.best.books);
  const stake = held ? null : formatStake(side.best.stakes.get(choice.book), kinds.get(choice.book) ?? null);
  const label = held
    ? `${side.sideLabel}, paused at ${side.display}. Compare all books`
    : `${side.sideLabel}, best ${side.display}${side.ties > 0 ? ` and ${side.ties} more` : ""}${stakeLabel(stake)}. Compare all books`;
  const shell = cn(
    "relative grid w-full min-w-0 overflow-hidden rounded-md border border-transparent text-foreground",
    compact ? "h-11 bg-white/[0.03]" : "h-[30px] bg-white/[0.028]",
    selected && "border-primary/55 bg-primary/10",
  );
  const hit = "min-w-0 transition-colors duration-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40";
  const arrow = held ? null : flash === "up" ? (
    <svg width="8" height="8" viewBox="0 0 8 8" aria-hidden="true" className="text-accent-value"><path d="M4 1.5 7 6H1z" fill="currentColor" /></svg>
  ) : flash === "down" ? (
    <svg width="8" height="8" viewBox="0 0 8 8" aria-hidden="true" className="text-destructive"><path d="M4 6.5 1 2h6z" fill="currentColor" /></svg>
  ) : null;
  const chip = (
    <BetChip
      books={side.best.books}
      pick={choice}
      subject={bet.subject}
      what={`${side.sideLabel} ${side.display}`}
      logoSize={logoSize}
      ringColor={selected ? "#141b20" : ringColor}
      max={compact ? 1 : 2}
      stacked={compact}
      held={held}
      className={cn("h-full border-l border-white/[0.06] px-1.5 hover:bg-white/[0.065] active:bg-white/[0.08]", !compact && "min-w-[52px]")}
      onBet={() => bet.onBet(target, choice.book)}
      onPrefetch={bet.onPrefetch}
    />
  );
  if (compact) {
    return (
      <div className={shell} style={{ gridTemplateColumns: "minmax(0,1fr) auto" }}>
        <FlashOverlay flash={held ? null : flash} display={side.display} />
        <button
          type="button"
          onClick={onOpen}
          aria-label={label}
          className={cn(hit, "flex flex-col items-center justify-center gap-px px-1 hover:bg-white/[0.06] active:bg-white/[0.08]")}
        >
          {side.lineLabel && <span className={cn("data-num whitespace-nowrap text-[11px] leading-[14px]", held ? "text-zinc-600" : "text-zinc-400")}>{side.lineLabel}</span>}
          <span className={cn("data-num flex max-w-full items-center gap-1 text-[13px] font-semibold leading-4", held ? "text-zinc-500" : "text-foreground")}>
            <span>{side.display}</span>
            {arrow}
          </span>
          <StakeText stake={stake} />
        </button>
        {chip}
      </div>
    );
  }
  return (
    <div className={shell} style={{ gridTemplateColumns: "minmax(0,1fr) auto" }}>
      <FlashOverlay flash={held ? null : flash} display={side.display} />
      <button
        type="button"
        onClick={onOpen}
        aria-label={label}
        className={cn(hit, "grid items-center px-2 hover:bg-white/[0.065]")}
        style={{ gridTemplateColumns: side.lineLabel ? "44px minmax(0,1fr)" : "0px minmax(0,1fr)" }}
      >
        <span className={cn("data-num truncate whitespace-nowrap text-[11px]", held ? "text-zinc-600" : "text-zinc-400")}>{side.lineLabel}</span>
        <span className="flex min-w-0 flex-col items-start">
          <span className={cn("data-num flex min-w-0 items-center gap-1 text-[14px] font-semibold leading-4 tracking-[-0.01em]", held ? "text-zinc-500" : "text-foreground")}>
            <span>{side.display}</span>
            {arrow}
          </span>
          <StakeText stake={stake} />
        </span>
      </button>
      {chip}
    </div>
  );
}
